import { useEffect, useState } from "react";
import "./AddTheme.css";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from "react-redux";
import { 
  UilPalette, UilPlus, UilTrashAlt, UilGrids, 
  UilTrash, UilTimes, UilEdit, UilCheck, UilTimesCircle,
  UilSearch
} from "@iconscout/react-unicons";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' }
});

export const AddTheme = () => {
  const { currentUser } = useSelector((state) => state.auth);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("draft:add-theme") || ""; } catch { return ""; }
  });
  const [themes, setThemes] = useState([]);
  const [filteredThemes, setFilteredThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingThemeId, setDeletingThemeId] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit mode states
  const [editingThemeId, setEditingThemeId] = useState(null);
  const [editingThemeTitle, setEditingThemeTitle] = useState("");


  useEffect(() => {
    const rawToken = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;
    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
      console.log(" Admin token synced to axios (AddTheme)");
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  useEffect(() => {
    localStorage.setItem("draft:add-theme", theme);
  }, [theme]);

  const handleAddTheme = (e) => {
    setTheme(e.target.value);
  };

  const addThemeFunction = async (title) => {
    if (!title.trim()) {
      toast.error("Veuillez entrer un nom de thème", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post("/themes/", { title });

      console.log("Thème ajouté :", response.data);
      toast.success('Thème ajouté avec succès!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Reset form
      localStorage.removeItem("draft:add-theme");
      setTheme("");
      
      // Refresh themes list
      loadThemes();
      
    } catch (error) {
      console.error("Erreur lors de l'ajout du thème :", error);
      toast.error(error.response?.data?.message || "Erreur lors de l'ajout", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Start editing theme
  const startEditTheme = (themeItem) => {
    setEditingThemeId(themeItem._id);
    setEditingThemeTitle(themeItem.title);
  };
  
  // Cancel editing theme
  const cancelEditTheme = () => {
    setEditingThemeId(null);
    setEditingThemeTitle("");
  };
  
  // Save edited theme
  const saveEditTheme = async (themeId) => {
    if (!editingThemeTitle.trim()) {
      toast.error("Le nom du thème ne peut pas être vide", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    try {
      await api.put(`/themes/${themeId}`, {
        title: editingThemeTitle
      });
      
      toast.success("Thème modifié avec succès", {
        position: "top-right",
        autoClose: 3000,
      });
      
      cancelEditTheme();
      loadThemes();
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la modification", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };
  
  // Delete theme
  const handleDeleteTheme = async (themeId, themeTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le thème "${themeTitle}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    setDeletingThemeId(themeId);
    
    try {
      await api.delete(`/themes/${themeId}`);
      toast.success(`Thème "${themeTitle}" supprimé avec succès`, {
        position: "top-right",
        autoClose: 3000,
      });
      loadThemes();
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error(error.response?.data?.message || "Erreur lors de la suppression", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setDeletingThemeId(null);
    }
  };
  
  const loadThemes = async () => {
    try {
      const response = await api.get("/themes");
      setThemes(response.data.themes);
      setFilteredThemes(response.data.themes);
    } catch (error) {
      console.error("Erreur de chargement des thèmes", error);
      toast.error("Erreur lors du chargement des thèmes", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    loadThemes();
  }, []);

  // Filter themes based on search term (starts with, case insensitive)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredThemes(themes);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = themes.filter(themeItem => 
        themeItem.title.toLowerCase().startsWith(term)
      );
      setFilteredThemes(filtered);
    }
  }, [searchTerm, themes]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  // Function to highlight matching text
  const highlightText = (text, search) => {
    if (!search.trim()) return text;
    const term = search.toLowerCase().trim();
    if (text.toLowerCase().startsWith(term)) {
      const matchPart = text.substring(0, term.length);
      const restPart = text.substring(term.length);
      return (
        <>
          <mark>{matchPart}</mark>
          {restPart}
        </>
      );
    }
    return text;
  };

  return (
    <div className="add-theme-container">
      <div className="add-theme-grid">
        {/* Left Column - Add Theme Form */}
        <div className="add-theme-grid-item form-section">
          
          {!currentUser && (
            <div className="auth-alert">
              ⚠️ Vous devez être connecté en tant qu'administrateur
            </div>
          )}
          
          <div className="form-group">
            <h4>Nom du thème</h4>
            <div className="add-theme-input">
              <UilPalette className="input-icon" size={18} />
              <input 
                onChange={handleAddTheme}
                className="add-theme-input-text" 
                placeholder="Ex: Technologie, Nature, Art, Sport..."
                value={theme}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addThemeFunction(theme);
                  }
                }}
              />
            </div>
            <div className="field-note">
              🎨 Nom du thème pour catégoriser les quiz
            </div>
          </div>
          
          <div className="theme-publish-buttons">
            <button 
              onClick={() => addThemeFunction(theme)} 
              className="submit-btn"
              disabled={loading || !theme.trim()}
            >
              {loading ? "Envoi en cours..." : "📌 Publier le thème"}
            </button>
          </div>
        </div>
        
        {/* Right Column - Existing Themes List with Search */}
        <div className="add-theme-grid-item list-section">
          <h2 className="section-title">
            <UilGrids size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Thèmes existants
          </h2>
          
          {/* Search Bar */}
          <div className="search-bar-container">
            <div className="search-input-wrapper">
              <UilSearch className="search-icon-theme" size={18} />
              <input
                type="text"
                placeholder="Tapez la première lettre du thème..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-theme-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn" title="Effacer la recherche">
                  <UilTimes size={16} />
                </button>
              )}
            </div>
          </div>
          
          {/* Search results info */}
          {searchTerm && (
            <div className="search-results-info">
              <UilSearch size={14} />
              <span>
                {filteredThemes.length} thème(s) commençant par "{searchTerm}"
              </span>
            </div>
          )}
          
          <div className="themes-list-container">
            {filteredThemes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <UilPalette size={64} />
                </div>
                {searchTerm ? (
                  <>
                    <p>Aucun thème commençant par "{searchTerm}"</p>
                    <div className="field-note">Essayez une autre lettre</div>
                  </>
                ) : (
                  <>
                    <p>Aucun thème disponible</p>
                    <div className="field-note">Créez votre premier thème ci-contre</div>
                  </>
                )}
              </div>
            ) : (
              <ul className="themes-list">
                {filteredThemes.map((themeItem) => (
                  <li key={themeItem._id} className="theme-item">
                    <div className="theme-header">
                      <UilPalette className="theme-icon" size={20} />
                      
                      {editingThemeId === themeItem._id ? (
                        <div className="edit-theme-container">
                          <input
                            type="text"
                            value={editingThemeTitle}
                            onChange={(e) => setEditingThemeTitle(e.target.value)}
                            className="edit-theme-input"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEditTheme(themeItem._id);
                              if (e.key === "Escape") cancelEditTheme();
                            }}
                          />
                          <button
                            onClick={() => saveEditTheme(themeItem._id)}
                            className="save-edit-btn"
                            title="Enregistrer"
                          >
                            <UilCheck size={16} />
                          </button>
                          <button
                            onClick={cancelEditTheme}
                            className="cancel-edit-btn"
                            title="Annuler"
                          >
                            <UilTimesCircle size={16} />
                          </button>
                        </div>
                      ) : (
                        <strong className="theme-title">
                          {highlightText(themeItem.title, searchTerm)}
                        </strong>
                      )}
                      
                      <div className="theme-actions">
                        {!editingThemeId && (
                          <>
                            <button
                              onClick={() => startEditTheme(themeItem)}
                              className="edit-theme-btn"
                              title="Modifier le thème"
                            >
                              <UilEdit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteTheme(themeItem._id, themeItem.title)}
                              className="delete-theme-btn"
                              disabled={deletingThemeId === themeItem._id}
                              title="Supprimer le thème"
                            >
                              <UilTrash size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
